'use client';

import { useEffect, useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface WorkingStyleProps {
  language: 'en' | 'es';
}

const WorkingStyle = ({ language }: WorkingStyleProps) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        });
      },
      { threshold: 0.1 }
    );

    const element = document.getElementById('working-style');
    if (element) observer.observe(element);

    return () => observer.disconnect();
  }, []);

  const principles = {
    en: [
      {
        id: 'principle_async',
        number: '1',
        title: 'Async Communication',
        description: 'We respond in writing. No surprise calls.',
        icon: 'ChatBubbleLeftRightIcon',
      },
      {
        id: 'principle_pricing',
        number: '2',
        title: 'Clear Pricing',
        description: "You\'ll know the cost before we start. Always.",
        icon: 'CurrencyDollarIcon',
      },
      {
        id: 'principle_decisions',
        number: '3',
        title: 'Fast Decisions',
        description: 'If we can answer today, we do today.',
        icon: 'BoltIcon',
      },
    ],
    es: [
      {
        id: 'principle_async',
        number: '1',
        title: 'Comunicación Asíncrona',
        description: 'Respondemos por escrito. Sin llamadas sorpresa.',
        icon: 'ChatBubbleLeftRightIcon',
      },
      {
        id: 'principle_pricing',
        number: '2',
        title: 'Precios Claros',
        description: 'Sabrás el costo antes de empezar. Siempre.',
        icon: 'CurrencyDollarIcon',
      },
      {
        id: 'principle_decisions',
        number: '3',
        title: 'Decisiones Rápidas',
        description: 'Si podemos responder hoy, lo hacemos hoy.',
        icon: 'BoltIcon',
      },
    ],
  };

  return (
    <section id="working-style" className="py-20 px-6 bg-muted">
      <div className="max-w-screen-xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {principles[language].map((principle, index) => (
            <div
              key={principle.id}
              className={`p-8 bg-background border hairline border-border sharp ${
                isVisible ? 'animate-fadeInUp' : 'opacity-0'
              }`}
              style={{ animationDelay: `${index * 100}ms` }}
            >
              <div className="flex items-start gap-4 mb-4">
                <span className="font-mono text-4xl font-bold text-muted-foreground">
                  {principle.number}
                </span>
                <Icon name={principle.icon as any} size={32} className="text-primary" />
              </div>
              <h3 className="text-xl font-bold text-foreground mb-2">
                {principle.title}
              </h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {principle.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default WorkingStyle;